import {Anchor, Box, Button, createStyles, Flex, Group, Image, Stack, Text, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import * as MfaDAO from 'auth/MfaDAO';
import {setLocationHref} from 'BenchlingUtil';

const useStyles = createStyles((theme) => ({
  card: {
    width: 380,
    maxWidth: '100%',
    backgroundColor: theme.white,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.xl,
    boxShadow:
      '0px 1px 1.5px rgba(0, 0, 0, 0.05), 0px 7px 3.5px rgba(0, 0, 0, 0.04), 0px 10px 7.5px rgba(0, 0, 0, 0.1)',
  },
}));

function AuthCard({children}: {children: React.ReactNode}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Box id="auth" className="auth-v2" h="100%">
      <Flex className="container" justify="center" align="center" h="100%">
        <Box className={classes.card}>{children}</Box>
      </Flex>
    </Box>
  );
}

export interface SetupMfaPageProps {
  mfaEnabled: boolean;
  qrCodeB64?: string;
  settingsUrl?: string;
  // True when the user was forced into MFA enrollment (login, SSO, or post-verify enforcement) rather
  // than setting it up voluntarily. In that flow we show the setup-finished screen and send them to the
  // server-provided redirect, instead of redirecting back to the user settings page.
  forcedEnrollment?: boolean;
}

export default function SetupMfaPage(props: SetupMfaPageProps): JSX.Element {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('/');

  async function handleSubmit(): Promise<void> {
    setError(null);
    try {
      const {success: confirmed, redirectUrl: nextUrl} = await MfaDAO.confirmMfaSetup(code);
      if (!confirmed) {
        setError('That code doesn’t match. Try a fresh code or set up again.');
        return;
      }
      if (props.forcedEnrollment) {
        // Forced enrollment: show the setup-finished screen. The user is now signed in (confirmMfaSetup
        // signs in the pre-login flows; post-verify was already signed in); "Go to Benchling" lands them
        // at the server-validated redirectUrl (honors the original next_url), falling back to the homepage.
        if (nextUrl) {
          setRedirectUrl(nextUrl);
        }
        setSetupComplete(true);
        return;
      }
      // Self-service setup from the user settings page: redirect back to settings.
      setLocationHref(props.settingsUrl ?? '/');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      setError('That code doesn’t match. Try a fresh code or set up again.');
    }
  }

  if (props.mfaEnabled || setupComplete) {
    return (
      <AuthCard>
        <Stack spacing="lg" align="center">
          <Image
            width={240}
            height={52}
            src="/static/img/bioblue_wordmark_500.png"
            className="brand-benchling-logo"
            alt="Benchling"
            fit="contain"
          />
          <Text size="lg" weight={600} ta="center">
            You have successfully set up MFA
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            To change this setting in the future, go to your{' '}
            <Anchor href={props.settingsUrl ?? '/'}>User Settings</Anchor> page from the user avatar in
            Benchling.
          </Text>
          <Button w="100%" component="a" href={redirectUrl}>
            Go to Benchling
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <Stack spacing="lg">
        <Flex justify="center">
          <Image
            width={240}
            height={52}
            src="/static/img/bioblue_wordmark_500.png"
            className="brand-benchling-logo"
            alt="Benchling"
            fit="contain"
          />
        </Flex>
        <Text size="md" weight={600}>
          Configure new multi-factor authentication
        </Text>
        <Stack spacing="md">
          <Stack spacing={0}>
            <Text size="sm" weight={600}>
              Step 1: Scan this code using an authenticator app
            </Text>
            <Text size="xs" c="dimmed">
              eg. Google Auth, Microsoft Authenticator, Authy
            </Text>
          </Stack>
          {props.qrCodeB64 && (
            <Flex justify="center">
              <Image
                src={`data:image/png;base64,${props.qrCodeB64}`}
                width={280}
                height={280}
                alt="MFA QR code"
                fit="contain"
              />
            </Flex>
          )}
          <Stack spacing={0}>
            <Text size="sm" weight={600}>
              Step 2: Enter the six digit code from the app
            </Text>
            <Text size="xs" c="dimmed">
              This will confirm setup was successful
            </Text>
          </Stack>
          <TextInput
            label="Authentication code"
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(e.currentTarget.value.replace(/\D/g, ''));
            }}
            inputMode="numeric"
            maxLength={6}
            error={error}
            styles={{input: {width: 121}}}
          />
        </Stack>
        <Group position="right" spacing="sm">
          <Button variant="secondary" component="a" href={props.settingsUrl ?? '/'}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={code.length !== 6}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </AuthCard>
  );
}
